import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { SimpleTabs } from "../../components/simplePage/simpleTabs.tsx";
import { SimpleFieldset } from "../../components/simplePage/simpleFieldset.tsx";
import { servicePost } from "../../api/client.ts";
import { FwRichTextarea } from "../../components/ui/fwRichTextarea.tsx";
import styles from "../../styles/formPage.module.scss";
import type { ProductEditorHandle, ProductEditorProps } from "./editProductShared.ts";

/* ── Types ──────────────────────────────────────────────── */

interface NeoProduct {
  ID: number;
  OperatorID: number;
  Name: string;
  BrandID: number;
  Description: string;
  Gender: number; // 0=None, 1=Male, 2=Female
  NewUntil: string;
  BuyPrice: number;
  BuyTax: number;
  RentalPrice: number;
  RentalTax: number;
  ImageUrl: string;
  SpecificationImage: string;
  TechnologyImage: string;
  PerformanceImage: string;
  CategoryIDs: string;
  RelationIDs: string;
}

interface Brand {
  ID: number;
  Name: string;
}

const GENDER_OPTIONS = [
  { value: 0, label: "None" },
  { value: 1, label: "Male" },
  { value: 2, label: "Female" },
];

/* ── Component ──────────────────────────────────────────── */

export const EditProductNeoCaddie = forwardRef<ProductEditorHandle, ProductEditorProps>(
  function EditProductNeoCaddie({ productId, onSaved, onStateChange }, ref) {
  const [product, setProduct] = useState<NeoProduct>(emptyProduct());
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [dirty, setDirty] = useState(false);
  const [categoryInput, setCategoryInput] = useState("");
  const [relationInput, setRelationInput] = useState("");

  const isNew = !productId;

  useEffect(() => {
    onStateChange?.({ saving, status, dirty });
  }, [saving, status, dirty, onStateChange]);

  // Load product and brands
  useEffect(() => {
    servicePost<Brand[]>("GetBrand", {}).then(r => {
      if (!r.bError && r.oData) setBrands(r.oData);
    });

    if (productId <= 0) {
      setProduct(emptyProduct());
      setDirty(false);
      return;
    }
    setLoading(true);
    servicePost<NeoProduct>("GetProductNeo", { id: productId })
      .then(result => {
        if (!result.bError && result.oData) {
          setProduct(result.oData);
          setDirty(false);
        } else {
          setStatus({ type: "error", text: "Failed to load product" });
        }
      })
      .finally(() => setLoading(false));
  }, [productId]);

  const handleChange = useCallback((field: keyof NeoProduct, value: string | number) => {
    setProduct(prev => ({ ...prev, [field]: value }));
    setDirty(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!product.Name.trim()) {
      setStatus({ type: "error", text: "Product name is required." });
      return;
    }
    setSaving(true);
    setStatus(null);
    try {
      const result = await servicePost<NeoProduct>("SaveProductNeo", { product });
      if (result.bError || !result.oData) {
        setStatus({ type: "error", text: result.sMessage ?? "Failed to save product" });
      } else {
        // Keep the price the user just submitted — the save response must not
        // blank the price fields (price is stored separately in S_Generic_Price),
        // or a later save would resubmit 0 and wipe it.
        setProduct(prev => ({
          ...result.oData!,
          BuyPrice: prev.BuyPrice, BuyTax: prev.BuyTax,
          RentalPrice: prev.RentalPrice, RentalTax: prev.RentalTax,
        }));
        setDirty(false);
        setStatus({ type: "success", text: isNew ? "Product Created" : "Product Updated" });
        onSaved();
      }
    } finally {
      setSaving(false);
    }
  }, [product, isNew, onSaved]);

  useImperativeHandle(ref, () => ({ save: handleSave }), [handleSave]);

  const tabs = useMemo(() => [
    {
      key: "product",
      label: "Product",
      children: (
        <ProductTab
          product={product}
          brands={brands}
          onChange={handleChange}
        />
      ),
    },
    {
      key: "pictures",
      label: "Pictures",
      children: <PicturesTab product={product} isNew={isNew} />,
    },
    {
      key: "categories",
      label: "Categories",
      children: (
        <CategoriesTab
          categoryIDs={product.CategoryIDs}
          categoryInput={categoryInput}
          setCategoryInput={setCategoryInput}
          onAdd={() => {
            if (categoryInput.trim()) {
              const existing = product.CategoryIDs ? product.CategoryIDs.split(",") : [];
              existing.push(categoryInput.trim());
              handleChange("CategoryIDs", existing.join(","));
              setCategoryInput("");
            }
          }}
        />
      ),
    },
    {
      key: "relations",
      label: "Relations",
      children: (
        <RelationsTab
          relationIDs={product.RelationIDs}
          relationInput={relationInput}
          setRelationInput={setRelationInput}
          onAdd={() => {
            if (relationInput.trim()) {
              const existing = product.RelationIDs ? product.RelationIDs.split(",") : [];
              existing.push(relationInput.trim());
              handleChange("RelationIDs", existing.join(","));
              setRelationInput("");
            }
          }}
        />
      ),
    },
  ], [product, brands, handleChange, isNew, categoryInput, relationInput]);

  if (loading) return <p>Loading...</p>;

  return <SimpleTabs tabs={tabs} />;
},
);

/* ── Tab content ───────────────────────────────────────── */

function ProductTab({ product, brands, onChange }: {
  product: NeoProduct;
  brands: Brand[];
  onChange: (field: keyof NeoProduct, value: string | number) => void;
}) {
  return (
    <div className={styles.formContainer}>
      <SimpleFieldset legend="Product Information">
        <div className={styles.fieldRowStacked}>
          <label>Name</label>
          <input className={styles.fieldInput} value={product.Name} onChange={e => onChange("Name", e.target.value)} />
        </div>
        <div className={styles.fieldRowStacked}>
          <label>Brand</label>
          <select className={styles.fieldSelect} value={product.BrandID} onChange={e => onChange("BrandID", parseInt(e.target.value))}>
            <option value={0}>Select brand...</option>
            {brands.map(b => <option key={b.ID} value={b.ID}>{b.Name}</option>)}
          </select>
        </div>
        <div className={styles.fieldRowStacked}>
          <label>Description</label>
          <FwRichTextarea
            value={product.Description}
            onChange={v => onChange("Description", v)}
            rows={4}
          />
          <p className={styles.hint}>Wrap text in &lt;fw&gt;…&lt;/fw&gt; to use fixed-width font for that block (e.g. for aligned nutrition tables).</p>
        </div>
        <div className={styles.fieldRowStacked}>
          <label>Gender</label>
          <div className={styles.radioGroup}>
            {GENDER_OPTIONS.map(g => (
              <label key={g.value}>
                <input
                  type="radio"
                  name="gender"
                  checked={product.Gender === g.value}
                  onChange={() => onChange("Gender", g.value)}
                />
                {g.label}
              </label>
            ))}
          </div>
        </div>
        <div className={styles.fieldRowStacked}>
          <label>New Until</label>
          <input className={styles.fieldInput} type="date" value={product.NewUntil} onChange={e => onChange("NewUntil", e.target.value)} />
        </div>
      </SimpleFieldset>

      <SimpleFieldset legend="Pricing">
        <div style={{ display: "flex", gap: "var(--space-3)" }}>
          <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
            <label>Buy Price</label>
            <input className={styles.fieldInput} type="number" step="0.01" min="0" value={product.BuyPrice} onChange={e => onChange("BuyPrice", Math.max(0, parseFloat(e.target.value) || 0))} />
          </div>
          <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
            <label>Buy Tax</label>
            <input className={styles.fieldInput} type="number" step="0.01" min="0" value={product.BuyTax} onChange={e => onChange("BuyTax", Math.max(0, parseFloat(e.target.value) || 0))} />
          </div>
        </div>
        <div style={{ display: "flex", gap: "var(--space-3)" }}>
          <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
            <label>Rental Price</label>
            <input className={styles.fieldInput} type="number" step="0.01" min="0" value={product.RentalPrice} onChange={e => onChange("RentalPrice", Math.max(0, parseFloat(e.target.value) || 0))} />
          </div>
          <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
            <label>Rental Tax</label>
            <input className={styles.fieldInput} type="number" step="0.01" min="0" value={product.RentalTax} onChange={e => onChange("RentalTax", Math.max(0, parseFloat(e.target.value) || 0))} />
          </div>
        </div>
      </SimpleFieldset>
    </div>
  );
}

function PicturesTab({ product, isNew }: { product: NeoProduct; isNew: boolean }) {
  return (
    <div className={styles.formContainer}>
      <SimpleFieldset legend="Product Image">
        <div className={styles.fieldRowStacked}>
          <label>Image</label>
          <input className={styles.fieldInput} value={product.ImageUrl} readOnly />
          {product.ImageUrl && (
            <div style={{ textAlign: "center", padding: "var(--space-2)" }}>
              <img src={product.ImageUrl} alt={product.Name} style={{ maxHeight: 200, maxWidth: 300, border: "1px solid var(--color-border-default)" }} />
            </div>
          )}
        </div>
        {isNew && <p className={styles.hint} style={{ color: "var(--color-error)" }}>Create the product before uploading images.</p>}
      </SimpleFieldset>

      <SimpleFieldset legend="Info Images">
        <InfoImage label="Specification Image" url={product.SpecificationImage} />
        <InfoImage label="Technology Image" url={product.TechnologyImage} />
        <InfoImage label="Performance Image" url={product.PerformanceImage} />
      </SimpleFieldset>
    </div>
  );
}

function InfoImage({ label, url }: { label: string; url: string }) {
  return (
    <div className={styles.fieldRowStacked}>
      <label>{label}</label>
      <input className={styles.fieldInput} value={url} readOnly />
      {url && (
        <div style={{ textAlign: "center", padding: "var(--space-1)" }}>
          <img src={url} alt={label} style={{ maxHeight: 120, maxWidth: 200, border: "1px solid var(--color-border-default)" }} />
        </div>
      )}
    </div>
  );
}

function CategoriesTab({ categoryIDs, categoryInput, setCategoryInput, onAdd }: {
  categoryIDs: string;
  categoryInput: string;
  setCategoryInput: (v: string) => void;
  onAdd: () => void;
}) {
  return (
    <div className={styles.formContainer}>
      <SimpleFieldset legend="Product Categories">
        <p className={styles.hint}>Categories support DisplayOrder, ForceHand, ForceLoft, ForceFlex, and ForceShaft options.</p>
        <div className={styles.inlineActions}>
          <input className={styles.fieldInput} value={categoryInput} onChange={e => setCategoryInput(e.target.value)} placeholder="Category ID..." />
          <button className={styles.secondaryBtn} onClick={onAdd}>Add</button>
        </div>
        {categoryIDs && (
          <div style={{ fontSize: "var(--text-xs)", marginTop: "var(--space-1)", color: "var(--color-text-secondary)" }}>
            Assigned: {categoryIDs}
          </div>
        )}
      </SimpleFieldset>
    </div>
  );
}

function RelationsTab({ relationIDs, relationInput, setRelationInput, onAdd }: {
  relationIDs: string;
  relationInput: string;
  setRelationInput: (v: string) => void;
  onAdd: () => void;
}) {
  return (
    <div className={styles.formContainer}>
      <SimpleFieldset legend="Product Relations">
        <div className={styles.inlineActions}>
          <input className={styles.fieldInput} value={relationInput} onChange={e => setRelationInput(e.target.value)} placeholder="Related Product ID..." />
          <button className={styles.secondaryBtn} onClick={onAdd}>Add</button>
        </div>
        {relationIDs && (
          <div style={{ fontSize: "var(--text-xs)", marginTop: "var(--space-1)", color: "var(--color-text-secondary)" }}>
            Relations: {relationIDs}
          </div>
        )}
      </SimpleFieldset>
    </div>
  );
}

/* ── Helpers ───────────────────────────────────────────── */

function emptyProduct(): NeoProduct {
  return {
    ID: 0, OperatorID: 0, Name: "", BrandID: 0,
    Description: "", Gender: 0, NewUntil: "",
    BuyPrice: 0, BuyTax: 0, RentalPrice: 0, RentalTax: 0,
    ImageUrl: "", SpecificationImage: "", TechnologyImage: "",
    PerformanceImage: "", CategoryIDs: "", RelationIDs: "",
  };
}
